import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Service from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';

const ALL_CARDS = [
  { id: 'supplements', label: 'Supplements' },
  { id: 'retest', label: 'Retest Semen Analysis' },
  { id: 'provider', label: 'Discuss with Your Provider' },
  { id: 'hormone-test', label: 'Hormone Test' },
  { id: 'medication', label: 'Medication' },
  { id: 'force-emergency-alert', label: 'Force Show Emergency Alert' },
  { id: 'hide-emergency-alert', label: 'Force Hide Emergency Alert' },
  { id: 'no-card', label: 'No Card' },
];

const NextStepCardsModal = ({ patient, onClose }) => {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // False until an admin saves a selection: the patient sees the cards the
  // copy grid rules pick, not the list ticked below.
  const [adminSet, setAdminSet] = useState(true);

  const DEFAULT_CARDS = ['supplements', 'retest', 'hormone-test'];

  useEffect(() => {
    Service.getNextStepCards(patient.id)
      .then((res) => {
        setSelected(res?.data?.visible_cards || DEFAULT_CARDS);
        setAdminSet(res?.data?.admin_set !== false);
      })
      .catch(() => setSelected(DEFAULT_CARDS))
      .finally(() => setLoading(false));
  }, [patient.id]);

  const toggle = (id) => {
    if (id === 'no-card') {
      setSelected((prev) => (prev.includes('no-card') ? prev.filter((c) => c !== 'no-card') : ['no-card']));
    } else {
      setSelected((prev) => {
        if (prev.includes('no-card')) return [id];
        return prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
      });
    }
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      showToast('error', 'Select at least one card');
      return;
    }
    setSubmitting(true);
    try {
      await Service.setNextStepCards(patient.id, selected);
      showToast('success', 'Next step cards updated');
      onClose();
    } catch {
      showToast('error', 'Failed to update cards');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetToRules = async () => {
    setSubmitting(true);
    try {
      await Service.resetNextStepCardsToRules(patient.id);
      showToast('success', 'Cards now follow the copy grid rules');
      onClose();
    } catch {
      showToast('error', 'Failed to reset cards');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Next Step Cards</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="patient-info">
            Patient:{' '}
            <strong>
              {patient.first_name} {patient.last_name}
            </strong>{' '}
            ({patient.email})
          </p>
          {!loading && (
            <div className={`alert py-2 px-3 ${adminSet ? 'alert-warning' : 'alert-info'}`}>
              {adminSet
                ? 'Admin override is active: the patient sees the cards ticked below.'
                : 'No override: the patient sees the cards picked by the copy grid rules. Saving a selection overrides them.'}
            </div>
          )}
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="offerings-list">
              {ALL_CARDS.map((card) => (
                <div key={card.id} className={`offering-item ${selected.includes(card.id) ? 'selected' : ''}`} onClick={() => toggle(card.id)}>
                  <input type="checkbox" checked={selected.includes(card.id)} onChange={() => toggle(card.id)} />
                  <label>{card.label}</label>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-danger" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {adminSet && (
            <button className="btn btn-outline-secondary" onClick={handleResetToRules} disabled={submitting || loading}>
              Reset to grid rules
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={submitting || loading}>
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

NextStepCardsModal.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NextStepCardsModal;
